import {
  CustomFieldCategoricalMultiple,
  CustomFieldCategoricalSingle,
  CustomFieldDefinition,
  LocationDTO,
  LocationMapper,
  ReportCancelReasons,
  ReportMapper,
  ReportTaskDTO,
  ReportType,
} from '@nx-smartmonkey/shared/domain';
import { blobToDataURL, dateFormat, dateFormatLongDate, dateTodayFormat } from '@nx-smartmonkey/shared/helpers';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Content, ContextPageSize, TDocumentDefinitions } from 'pdfmake/interfaces';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import footerLogo from '../assets/img/reports-pdf/footer_logo.png';
import { Project } from '../domain/project/Project';
import { CategoricalFieldValue } from '../domain/project/ProjectDTO';
import { Stop } from '../domain/stop/Stop';
import { StopStatus } from '../domain/stop/StopStatus';
import { TaskDTO } from '../domain/task/TaskDTO';
import { VehicleFlat } from '../domain/vehicle/VehicleFlat';
import { TaskMapper } from '../mappers/task/TaskMapper';
import { getStopColorByStatus } from '../pages/planner/plan-details/StatusBar';
import { intl } from './IntGlobalProvider';

// @ts-expect-error
pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface StopWithReport {
  label?: string;
  status: StopStatus;
  phone?: string;
  vehicle_label?: string;
  location?: LocationDTO;
  tasks?: TaskDTO[];
  report?: {
    stop_id: string;
    driver_id?: string; // TODO: DRIVER_ID SHOULD NOT BE OPTIONAL
    project_id: string;
    plan_id: string;
    type: ReportType;
    comments?: string;
    images: Array<string>;
    signature?: string;
    cancel_reason?: ReportCancelReasons;
    location?: LocationDTO;
    custom_fields?: Record<string, any>;
    created_at: Date;
    tasks?: ReportTaskDTO[];
  };
}

interface ReportStopItemsVisibility {
  phone: boolean;
  pictures?: boolean;
  comments?: boolean;
  cancelReason?: boolean;
  signature?: boolean;
  task_barcode?: boolean;
  task_comments?: boolean;
}

const getImage = async (name: string) => {
  const iconCache = new Map();
  if (!iconCache.has(name)) {
    iconCache.set(name, await blobToDataURL(await (await fetch(name)).blob()));
  }
  return iconCache.get(name);
};

const generateStopReportHeader = ({
  stopWithReport,
  visibility,
}: {
  stopWithReport: StopWithReport;
  visibility: ReportStopItemsVisibility;
}): Content[] => {
  return [
    {
      margin: [-20, -20, -20, 0],
      table: {
        widths: [`*`, 200],
        body: [
          [
            {
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
              table: {
                widths: [`*`],
                margin: [0, 0, 0, 0],
                border: [false, false, false, false],
                body: [
                  [
                    {
                      margin: [0, 3, 10, 0],
                      text: stopWithReport.label,
                      fontSize: 22,
                      border: [false, false, false, false],
                      alignment: `left`,
                      color: RoutalPalette.primary40,
                    },
                  ],
                  [
                    {
                      text: stopWithReport.location?.label,
                      margin: [0, 3, 10, 0],
                      fontSize: 10,
                      border: [false, false, false, false],
                      alignment: `left`,
                      color: RoutalPalette.primary40,
                    },
                  ],
                  [
                    {
                      text: visibility.phone ? stopWithReport.phone : ``,
                      margin: [0, 3, 10, 0],
                      border: [false, false, false, false],
                      fontSize: 12,
                      alignment: `left`,
                      color: RoutalPalette.primary40,
                    },
                  ],
                ],
              },
            },
            {
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
              table: {
                heights: [`*`, `*`, `*`],
                widths: [`*`],
                margin: [0, 0, 0, 0],
                border: [false, false, false, false],
                body: [
                  [
                    {
                      margin: [0, 5, 0, 0],
                      text: ``,
                      border: [false, false, false, false],
                      alignment: `right`,
                    },
                  ],
                  [
                    {
                      margin: [0, 3, 0, 0],
                      text: `${intl.formatMessage({ id: `stops_report.status` })}:`,
                      border: [false, false, false, false],
                      alignment: `right`,
                    },
                  ],
                  [
                    {
                      text: intl.formatMessage({ id: `stops_report.status.${stopWithReport.status}` }),
                      margin: [0, 3, 0, 0],
                      border: [false, false, false, false],
                      alignment: `right`,
                      style: `stopStatus`,
                      color:
                        stopWithReport.status !== `pending` ? getStopColorByStatus(stopWithReport.status) : `#b3b1b1`,
                    },
                  ],
                ],
              },
            },
          ],
        ],
      },
    },
    // Line
    {
      canvas: [
        {
          type: `line`,
          x1: -10,
          y1: 0,
          x2: 525,
          y2: 0,
          lineWidth: 1,
        },
      ],
    },
  ];
};

const generateStopReportFooter = ({
  stopWithReport,
  visibility,
}: {
  stopWithReport: StopWithReport;
  visibility: ReportStopItemsVisibility;
}): Content[] => {
  return [
    // Line
    // {
    //   canvas: [
    //     {
    //       type: `line`,
    //       x1: -10,
    //       y1: 0,
    //       x2: 525,
    //       y2: 0,
    //       lineWidth: 1,
    //     },
    //   ],
    //   margin: [0, 120, 0, 10],
    // },
    {
      unbreakable: true,
      margin: [-15, 120, -15, 0],
      table: {
        widths: [`*`, `*`, `*`, ...(visibility.signature ? [`*`] : [])],
        body: [
          [
            {
              text: intl.formatMessage({ id: `stops_report.footer.vehicle_label` }),
              border: [false, 1, false, false],
              alignment: `center`,
              marginTop: 10,
              color: RoutalPalette.primary60,
            },
            {
              text: intl.formatMessage({ id: `stops_report.footer.location_label` }),
              border: [false, 1, false, false],
              alignment: `center`,
              marginTop: 10,
              color: RoutalPalette.primary60,
            },
            {
              text: intl.formatMessage({ id: `stops_report.footer.date_and_time` }),
              border: [false, 1, false, false],
              alignment: `center`,
              marginTop: 10,
              color: RoutalPalette.primary60,
            },
            ...(visibility.signature
              ? [
                  {
                    text: intl.formatMessage({ id: `stops_report.footer.signature` }),
                    border: [false, 1, false, false],
                    alignment: `center`,
                    marginTop: 10,
                    color: RoutalPalette.primary60,
                  },
                ]
              : []),
          ],
          [
            {
              text: stopWithReport.vehicle_label ?? ``,
              border: [false, false, false, false],
              alignment: `center`,
              color: RoutalPalette.primary40,
            },
            {
              text:
                stopWithReport.report!.location?.lat && stopWithReport.report!.location?.lng
                  ? `${stopWithReport.report!.location.lat}, ${stopWithReport.report!.location.lng}`
                  : ``,
              border: [false, false, false, false],
              alignment: `center`,
              color: RoutalPalette.primary40,
            },
            {
              text: dateFormat(stopWithReport.report!.created_at),
              border: [false, false, false, false],
              alignment: `center`,
              color: RoutalPalette.primary40,
            },
            ...(visibility.signature
              ? [
                  {
                    image: stopWithReport.report!.signature,
                    border: [false, false, false, false],
                    fit: [150, 80],
                    alignment: `center`,
                  },
                ]
              : []),
          ],
        ],
      },
    },
  ];
};

const generateTasksContent = ({
  stopWithReport,
  visibility,
  customFieldsDefinitions,
}: {
  stopWithReport: StopWithReport;
  visibility: ReportStopItemsVisibility;
  customFieldsDefinitions: CustomFieldDefinition[];
}): Content[] => {
  if ((stopWithReport.report?.tasks ?? []).length === 0) {
    return [];
  }

  return [
    {
      margin: [-10, 60, -10, 0],
      text: intl.formatMessage({ id: `stops_report.tasks.title` }),
      style: `reportTitle`,
    },
    ...stopWithReport.report!.tasks!.map((task): Content[] => {
      const stopTask = stopWithReport.tasks?.find((stopTask) => stopTask.id === task.task_id);

      const content: Content[] = [
        // Line
        {
          margin: [0, 10, 0, 10],
          canvas: [
            {
              type: `line`,
              x1: -10,
              y1: 0,
              x2: 525,
              y2: 0,
              lineWidth: 1,
            },
          ],
        },
        {
          unbreakable: true,
          margin: [-15, 0, -15, 0],
          table: {
            widths: [120, `*`],
            body: [
              [
                {
                  text: intl.formatMessage({ id: `stops_report.tasks.task.label` }),
                  style: `reportTitle`,
                  border: [false, false, false, false],
                  alignment: `left`,
                },
                {
                  text: stopTask?.label,
                  border: [false, false, false, false],
                  alignment: `left`,
                },
              ],
            ],
          },
        },
        {
          unbreakable: true,
          margin: [-15, 10, -15, 0],
          table: {
            widths: [120, `*`],
            body: [
              [
                {
                  text: intl.formatMessage({ id: `stops_report.tasks.task.status` }),
                  style: `reportTitle`,
                  border: [false, false, false, false],
                  alignment: `left`,
                },
                {
                  text: intl.formatMessage({ id: `stops_report.tasks.task.status.${task.status}` }),
                  border: [false, false, false, false],
                  alignment: `left`,
                  color: task.status !== `pending` ? getStopColorByStatus(task.status) : `#b3b1b1`,
                },
              ],
            ],
          },
        },
      ];

      if (visibility.task_barcode) {
        content.push({
          unbreakable: true,
          margin: [-15, 10, -15, 0],
          table: {
            widths: [120, `*`],
            body: [
              [
                {
                  text: intl.formatMessage({ id: `stops_report.tasks.task.barcode` }),
                  style: `reportTitle`,
                  border: [false, false, false, false],
                  alignment: `left`,
                },
                {
                  text: stopTask?.barcode,
                  border: [false, false, false, false],
                  alignment: `left`,
                },
              ],
            ],
          },
        });
      }

      return [
        ...content,
        ...generateCustomFieldsContent({
          customFields: task.custom_fields,
          customFieldsDefinitions,
        }),
      ];
    }),
  ];
};

const generateCustomFieldsContent = ({
  customFields,
  customFieldsDefinitions,
}: {
  customFields?: Record<
    string,
    string | number | boolean | string[] | CustomFieldCategoricalSingle | CustomFieldCategoricalMultiple
  >;
  customFieldsDefinitions: CustomFieldDefinition[];
}): Content[] => {
  if (!customFields) {
    return [];
  }

  const visibleCustomFieldsDefinitions = customFieldsDefinitions.filter(
    (customFieldsDefinition) => customFields[customFieldsDefinition.custom_id]
  );

  return visibleCustomFieldsDefinitions.reduce(
    (amount: Content[], customFieldsDefinition: CustomFieldDefinition, index: number) => {
      const table: Content = {
        unbreakable: true,
        margin: [-15, 20, -15, 0],
        table: {
          body: [
            [
              {
                text: customFieldsDefinition.label,
                style: `reportTitle`,
                border: [false, false, false, false],
                alignment: `left`,
              },
            ],
          ],
        },
      };

      switch (customFieldsDefinition.field_type) {
        case `text`:
        case `numerical`: {
          table.table.body.push([
            {
              text: customFields[customFieldsDefinition.custom_id],
              border: [false, false, false, false],
              alignment: `left`,
              color: RoutalPalette.primary40,
            },
          ]);
          break;
        }
        case `boolean`: {
          table.table.body.push([
            {
              text: intl.formatMessage({ id: customFields[customFieldsDefinition.custom_id] ? `yes` : `no` }),
              border: [false, false, false, false],
              alignment: `left`,
              color: RoutalPalette.primary40,
            },
          ]);
          break;
        }
        case `categorical`: {
          const customFieldValues: CustomFieldCategoricalMultiple = customFieldsDefinition.multiple
            ? (customFields[customFieldsDefinition.custom_id] as CustomFieldCategoricalMultiple)
            : ([
                (customFields as Record<string, CustomFieldCategoricalSingle>)[customFieldsDefinition.custom_id],
              ] as CustomFieldCategoricalMultiple);

          const fieldValues = customFieldValues.reduce(
            (amount: string, customFieldValue: CategoricalFieldValue, index: number) =>
              `${amount} ${index !== 0 ? `,` : ``} ${customFieldValue.label}`,
            ``
          );

          table.table.body.push([
            {
              text: fieldValues,
              border: [false, false, false, false],
              alignment: `left`,
              color: RoutalPalette.primary40,
            },
          ]);
          break;
        }
        default: {
          return amount;
        }
      }

      return [...amount, table];
    },
    []
  );
};

const generateStopReportContent = ({
  stopWithReport,
  project,
  isLastStop,
}: {
  stopWithReport: StopWithReport;
  project: Project;
  isLastStop: boolean;
}) => {
  const stopHasReport = stopWithReport.report !== undefined;

  const visibility = {
    phone: project.view.client.phone,
    ...(project.tasks_enabled
      ? {
          task_barcode: project.view.task.barcode,
          task_comments: project.view.task.comments,
        }
      : {}),
  } as ReportStopItemsVisibility;

  if (!stopHasReport) {
    return [
      ...generateStopReportHeader({ stopWithReport: stopWithReport, visibility }),
      {
        text: intl.formatMessage({ id: `stops_report.no_report` }),
        style: `reportTitle`,
        margin: [-10, 20, -10, 0],
        border: [false, false, false, false],
        alignment: `left`,
      },
      ...(!isLastStop
        ? [
            {
              text: ` `,
              // title: `spacer`,
              pageBreak: `before`,
            },
          ]
        : []),
    ] as Content[];
  }

  const statusIsCanceled = stopWithReport.status === `canceled`;

  visibility.pictures =
    stopWithReport.report!.images.length > 0 &&
    (statusIsCanceled
      ? Boolean(project.drivers.stop_canceled_fields.find((field) => field.id === `images` && !field.is_custom_field))
      : Boolean(
          project.drivers.stop_completed_fields.find((field) => field.id === `images` && !field.is_custom_field)
        ));

  visibility.comments =
    stopWithReport.report!.comments !== undefined &&
    (statusIsCanceled
      ? Boolean(project.drivers.stop_canceled_fields.find((field) => field.id === `comments` && !field.is_custom_field))
      : Boolean(
          project.drivers.stop_completed_fields.find((field) => field.id === `comments` && !field.is_custom_field)
        ));

  visibility.cancelReason =
    statusIsCanceled &&
    stopWithReport.report!.cancel_reason !== undefined &&
    Boolean(
      project.drivers.stop_canceled_fields.find((field) => field.id === `cancel_reason` && !field.is_custom_field)
    );

  visibility.signature =
    !statusIsCanceled &&
    Boolean(
      project.drivers.stop_completed_fields.find((field) => field.id === `signature` && !field.is_custom_field)
    ) &&
    stopWithReport.report!.signature !== undefined;

  return [
    ...generateStopReportHeader({ stopWithReport: stopWithReport, visibility }),
    // Comments
    ...(visibility.comments
      ? [
          {
            margin: [-15, 20, -15, 0],
            table: {
              body: [
                [
                  {
                    text: intl.formatMessage({ id: `stops_report.completed.comments` }),
                    style: `reportTitle`,
                    border: [false, false, false, false],
                    alignment: `left`,
                  },
                ],
                [
                  {
                    text: stopWithReport.report!.comments,
                    border: [false, false, false, false],
                    alignment: `left`,
                    color: RoutalPalette.primary40,
                  },
                ],
              ],
            },
          },
        ]
      : []),
    ...(visibility.cancelReason
      ? [
          // Cancelation reason
          {
            margin: [-15, 20, -15, 0],
            table: {
              body: [
                [
                  {
                    text: intl.formatMessage({ id: `stops_report.canceled.reason` }),
                    style: `reportTitle`,
                    border: [false, false, false, false],
                    alignment: `left`,
                  },
                ],
                [
                  {
                    text: intl.formatMessage({
                      id: `stops_report.canceled.reason.${stopWithReport.report!.cancel_reason}`,
                    }),
                    border: [false, false, false, false],
                    alignment: `left`,
                    color: RoutalPalette.primary40,
                  },
                ],
              ],
            },
          },
        ]
      : []),

    ...(visibility.pictures
      ? [
          {
            text: intl.formatMessage({ id: `stops_report.completed.images` }),
            style: `reportTitle`,
            // [left, top, right, bottom]
            margin: [-10, 20, -10, 0],
            border: [false, false, false, false],
            alignment: `left`,
          },
          ...stopWithReport.report!.images.map((reportImage, index: number) => {
            return {
              image: reportImage,
              width: 300,
              alignment: `center`,
              margin: [-10, 10, -10, index === stopWithReport.report!.images.length - 1 ? 20 : 0],
            };
          }),
        ]
      : []),
    ...generateCustomFieldsContent({
      customFields: stopWithReport.report!.custom_fields ?? {},
      customFieldsDefinitions:
        (statusIsCanceled
          ? project.custom_fields?.service_report_canceled
          : project.custom_fields?.service_report_completed) ?? [],
    }),
    // TODO: TASKS (with custom fields)
    ...(project.tasks_enabled
      ? generateTasksContent({
          stopWithReport: stopWithReport,
          visibility,
          customFieldsDefinitions:
            (statusIsCanceled
              ? project.custom_fields?.service_report_task_canceled
              : project.custom_fields?.service_report_task_completed) ?? [],
        })
      : []),
    ...generateStopReportFooter({ stopWithReport: stopWithReport, visibility }),
    ...(!isLastStop
      ? [
          {
            text: ` `,
            // title: `spacer`,
            pageBreak: `before`,
          },
        ]
      : []),
  ] as Content[];
};

interface ExportReportsToPdfProps {
  stops: Stop[];
  filename?: string;
  project: Project;
  projectVehicles: VehicleFlat[];
}

/**
 * This exports the stops current report
 * @param stops
 * @param filename
 * @param project
 */
export const exportReportsToPdf = async ({ stops, filename, project, projectVehicles }: ExportReportsToPdfProps) => {
  // Prepare stops reports to be exported
  const stopsWithReport: StopWithReport[] = await Promise.all(
    stops.map(async (stop) => {
      const currentReport = stop.getCurrentReport();

      const vehicle = projectVehicles.find((projectVehicle) => projectVehicle.id === stop.vehicle_id);

      if (!currentReport) {
        return {
          label: stop.label,
          status: stop.status,
          phone: stop.phone,
          vehicle_label: vehicle ? vehicle.label : undefined,
          location: stop.location ? LocationMapper.toDTO(stop.location) : undefined,
        };
      }

      const { images, signature, ...report } = ReportMapper.toDTO(currentReport!);

      const reportImages = await Promise.all(
        (images ?? []).map(async (image) => await getImage(`${image.url}/public`))
      );

      const reportSignature = signature ? await getImage(`${signature.url}/public`) : undefined;

      return {
        label: stop.label,
        status: stop.status,
        phone: stop.phone,
        vehicle_label: vehicle ? vehicle.label : undefined,
        location: stop.location ? LocationMapper.toDTO(stop.location) : undefined,
        tasks: stop.tasks ? stop.tasks.map(TaskMapper.toDTO) : undefined,
        report: {
          ...report,
          images: reportImages.filter(
            (image) => !image.includes(`data:image/gif;`) && !image.includes(`data:image/webp;`)
          ),
          signature: reportSignature,
        },
      };
    })
  );

  const footerLogoData = await getImage(footerLogo);

  const docDefinition = {
    // [left, top, right, bottom]
    // pageMargins: [0, 0, 0, 0],
    footer: (currentPage: number, pageCount: number, pageSize: ContextPageSize) => {
      return {
        margin: [0, 0, 0, 0],
        border: [false, false, false, false],
        table: {
          style: `footer`,
          heights: [35],
          widths: [15, 160, `*`, 40],
          border: [false, false, false, false],
          body: [
            [
              {
                image: footerLogoData,
                fit: [20, 20],
                fillColor: RoutalPalette.primary40,
                margin: [7, 8, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: `Powered by Routal®`,
                margin: [10, 12, 0, 0],
                border: [false, false, false, false],
                alignment: `left`,
                style: `footerText`,
                fillColor: RoutalPalette.primary40,
              },
              {
                text: intl.formatMessage({ id: `stops_report.generated_at` }, { time: dateTodayFormat() }),
                margin: [0, 12, 0, 0],
                fontSize: 10,
                border: [false, false, false, false],
                alignment: `right`,
                style: `footerText`,
                fillColor: RoutalPalette.primary40,
              },
              {
                text: `${currentPage}/${pageCount}`,
                margin: [6, 10, 0, 0],
                border: [false, false, false, false],
                alignment: `right`,
                style: `footerText`,
                fillColor: RoutalPalette.primary40,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      };
    },
    content: stopsWithReport.map((stopWithReport, index: number) =>
      generateStopReportContent({
        stopWithReport,
        project,
        isLastStop: index === stopsWithReport.length - 1,
      })
    ),
    styles: {
      footer: {
        backgroundColor: RoutalPalette.primary40,
      },
      footerText: {
        color: `white`,
      },
      mainTable: {
        height: `100%`,
      },
      divider: {
        height: `2px`,
        backgroundColor: RoutalPalette.primary40,
        width: `100%`,
      },
      stopStatus: {
        textTransform: `capitalize`,
        fontSize: 16,
      },
      reportTitle: {
        fontSize: 13,
        color: RoutalPalette.primary40,
        fontWeight: 500,
      },
    },
  } as TDocumentDefinitions;

  const reportsPdf = pdfMake.createPdf(docDefinition);
  reportsPdf.download(`${filename !== undefined ? filename : `Planning`}_${dateFormatLongDate(new Date())}.pdf`);
  // reportsPdf.open();
};
